import { ZonedDateTimeFormatter, zonedEndOfDay, zonedStartOfDay } from '@csp/csp-common-date-util';
import { UserId, type ZonedDateTime } from '@csp/csp-common-model';
import type { ScheduleRequestRef, ScheduleVersionRequestRef } from '@csp/csp-common-scheduling';
import { DeviceObservation as DeviceObservationEnum } from '@csp/device-catalog';
import {
  DeviceObservationCriteriaFieldV1,
  DeviceObservationQueryCriterionV1,
  DeviceObservationQueryTypeV1,
} from '@csp/dmdp-api-device-dto';
import { DeviceSessionCode } from '../model/DeviceSessionCode';

const byUserId = (userId: UserId): DeviceObservationQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceObservationCriteriaFieldV1.USER_ID,
    type: DeviceObservationQueryTypeV1.EQ,
    values: [userId],
  },
});

const byUserIds = (userIds: UserId[]): DeviceObservationQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceObservationCriteriaFieldV1.USER_ID,
    type: DeviceObservationQueryTypeV1.IN,
    values: userIds,
  },
});

const byNonConsentData = (nonConsentData: boolean): DeviceObservationQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceObservationCriteriaFieldV1.NON_CONSENT_DATA,
    type: DeviceObservationQueryTypeV1.EQ,
    values: [nonConsentData],
  },
});

const byStatus = (status: 'SCHEDULED' | 'UNSCHEDULED' | 'MISSED'): DeviceObservationQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceObservationCriteriaFieldV1.SCHEDULE_REQUEST_STATUS,
    type: DeviceObservationQueryTypeV1.EQ,
    values: [status],
  },
});

const byScheduleRequestRef = (requestRefs: readonly ScheduleRequestRef[]): DeviceObservationQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceObservationCriteriaFieldV1.SCHEDULE_REQUEST_REF_REQUEST_CODE,
    type: DeviceObservationQueryTypeV1.IN,
    values: [...requestRefs.map(({ requestCode }) => requestCode)],
  },
});

const byScheduleVersionRequestRef = (requestRef: ScheduleVersionRequestRef): DeviceObservationQueryCriterionV1[] => [
  byScheduleRequestRef([requestRef]),
  {
    fieldCriterion: {
      field: DeviceObservationCriteriaFieldV1.SCHEDULE_REQUEST_REF_SCHEDULE_CODE,
      type: DeviceObservationQueryTypeV1.EQ,
      values: [requestRef.scheduleCode],
    },
  },
  {
    fieldCriterion: {
      field: DeviceObservationCriteriaFieldV1.SCHEDULE_REQUEST_REF_VERSION_CODE,
      type: DeviceObservationQueryTypeV1.EQ,
      values: [requestRef.versionCode],
    },
  },
];

const bySessionCodes = (sessionCodes: readonly DeviceSessionCode[]): DeviceObservationQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceObservationCriteriaFieldV1.SESSION_CODE,
    type: DeviceObservationQueryTypeV1.IN,
    values: [...sessionCodes],
  },
});

const bySessionCode = (sessionCode: DeviceSessionCode): DeviceObservationQueryCriterionV1 =>
  bySessionCodes([sessionCode]);

const byTrendRecord = (): DeviceObservationQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceObservationCriteriaFieldV1.REFS_VALUE,
    type: DeviceObservationQueryTypeV1.NOT_IN,
    values: [DeviceObservationEnum.MASIMO_9809_PULSE_OX_STREAM, DeviceObservationEnum.MASIMO_9909_PULSE_OX_STREAM],
  },
});

const bySyncTimestampWithin = (start: ZonedDateTime, end: ZonedDateTime): DeviceObservationQueryCriterionV1[] => {
  const startDate = ZonedDateTimeFormatter.toZonedDateTimeString(zonedStartOfDay(start));
  const endDate = ZonedDateTimeFormatter.toZonedDateTimeString(zonedEndOfDay(end));

  return [
    {
      fieldCriterion: {
        field: DeviceObservationCriteriaFieldV1.SYNCHRONIZATION_TIMESTAMP,
        type: DeviceObservationQueryTypeV1.GREATER_THAN_EQ,
        values: [startDate],
      },
    },
    {
      fieldCriterion: {
        field: DeviceObservationCriteriaFieldV1.SYNCHRONIZATION_TIMESTAMP,
        type: DeviceObservationQueryTypeV1.LESS_THAN_EQ,
        values: [endDate],
      },
    },
  ];
};

const bySyncTimestampWithinExact = (start: ZonedDateTime, end: ZonedDateTime): DeviceObservationQueryCriterionV1[] => {
  const startDate = ZonedDateTimeFormatter.toZonedDateTimeString(start);
  const endDate = ZonedDateTimeFormatter.toZonedDateTimeString(end);

  return [
    {
      fieldCriterion: {
        field: DeviceObservationCriteriaFieldV1.SYNCHRONIZATION_TIMESTAMP,
        type: DeviceObservationQueryTypeV1.GREATER_THAN_EQ,
        values: [startDate],
      },
    },
    {
      fieldCriterion: {
        field: DeviceObservationCriteriaFieldV1.SYNCHRONIZATION_TIMESTAMP,
        type: DeviceObservationQueryTypeV1.LESS_THAN_EQ,
        values: [endDate],
      },
    },
  ];
};

const bySyncTimestampBefore = (date: ZonedDateTime): DeviceObservationQueryCriterionV1 => {
  const formattedDate = ZonedDateTimeFormatter.toZonedDateTimeString(date);

  return {
    fieldCriterion: {
      field: DeviceObservationCriteriaFieldV1.SYNCHRONIZATION_TIMESTAMP,
      type: DeviceObservationQueryTypeV1.LESS_THAN,
      values: [formattedDate],
    },
  };
};

const bySyncTimestampAfter = (date: ZonedDateTime): DeviceObservationQueryCriterionV1 => {
  const formattedDate = ZonedDateTimeFormatter.toZonedDateTimeString(date);

  return {
    fieldCriterion: {
      field: DeviceObservationCriteriaFieldV1.SYNCHRONIZATION_TIMESTAMP,
      type: DeviceObservationQueryTypeV1.GREATER_THAN,
      values: [formattedDate],
    },
  };
};

const bySyncTimestamp = (date: ZonedDateTime): DeviceObservationQueryCriterionV1[] =>
  bySyncTimestampWithin(zonedStartOfDay(date), zonedEndOfDay(date));

const byInvalidated = (invalidated: boolean): DeviceObservationQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceObservationCriteriaFieldV1.INVALIDATED,
    type: DeviceObservationQueryTypeV1.EXISTS,
    values: [invalidated],
  },
});

const byDeviceModelCode = (deviceModelCodes: string[]): DeviceObservationQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceObservationCriteriaFieldV1.DEVICE_MODEL_CODE,
    type: DeviceObservationQueryTypeV1.IN,
    values: deviceModelCodes,
  },
});

export const DeviceObservationQuery = {
  byScheduleRequestRef,
  byScheduleVersionRequestRef,
  bySessionCode,
  bySessionCodes,
  byStatus,
  bySyncTimestamp,
  bySyncTimestampWithin,
  bySyncTimestampWithinExact,
  bySyncTimestampBefore,
  bySyncTimestampAfter,
  byTrendRecord,
  byUserId,
  byUserIds,
  byInvalidated,
  byDeviceModelCode,
  byNonConsentData,
};
